import { localEventDate, localEventParts } from '../../shared/eventTimezones';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CakeIcon from '@mui/icons-material/Cake';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CelebrationIcon from '@mui/icons-material/Celebration';
import EventIcon from '@mui/icons-material/Event';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentsIcon from '@mui/icons-material/Payments';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import SportsTennisIcon from '@mui/icons-material/SportsTennis';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { supabase } from '../config/supabase';
import { useAuditUserId, useAuth } from '../auth/AuthContext';
import { lookupLabel } from '../data/lookups';
import type { LookupState, MatchEventRecord, TeamEventRecord, TeamPermissionFeature, TimeFormat, UserPreferences } from '../types';
import { buildCaptainWelcomeMessage, opposingCaptainNames, type CommunicationTeam } from '../utils/communications';
import {
  dismissCommandCenterNotification,
  loadCommandCenterNotifications,
  type CommandCenterNotification,
  type NotificationSeverity,
} from '../utils/notificationEngine';
import { loadParticipationReportRows } from '../utils/participationReport';
import { setProviderScoreWarningDisposition } from '../utils/providerScoreWarning';
import { formatPlayerName } from '../utils/playerPreferences';
import { lookupTeamAccentColor } from '../utils/teamColors';
import { isConfirmableTeamEvent, teamEventConfirmationKey } from '../utils/teamEventConfirmation';
import { formatTime } from '../utils/timeFormat';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import { summarizeGroupMeOnlyRsvps } from '../utils/groupmeOnlyRsvp';
import { responsibleCommandCenterTeamIds } from '../utils/commandCenterResponsibility';
import { TeamIcon } from './TeamIcon';
import { CaptainAddressBook } from './CaptainAddressBook';
import { SeasonSyncReviewDialog } from './SeasonSyncReviewDialog';

type CommandCenterProps = {
  lookups: LookupState;
  onOpenMatch: (matchId: string) => void;
  onOpenMatchScores: (matchId: string) => void;
  onOpenLineup: (matchId: string) => void;
  onOpenOutstandingFees: () => void;
  onOpenPaymentInbox: () => void;
  onOpenParticipationReport: (teamId?: string | null) => void;
  onOpenPlayers: () => void;
  onOpenTeams: () => void;
  onOpenTeamDetail: (teamId: string) => void;
  onOpenAvailability: (teamId?: string | null) => void;
  onOpenTeamRoster: (teamId?: string | null) => void;
  onOpenCommunications: (matchId?: string | null) => void;
  onOpenMatches: () => void;
  onOpenTeamEvents: () => void;
  onOpenTeamEvent: (eventId: string) => void;
  onOpenNotificationSettings: () => void;
  onOpenTennisRecordSync: () => void;
  captainName: string;
  timeFormat: TimeFormat;
  userPreferences: UserPreferences;
  writableTeamIds: string[];
  isOwner: boolean;
  featureAccess: Record<TeamPermissionFeature, boolean>;
};

type CommandCenterFeatureAccess = Record<TeamPermissionFeature, boolean>;

type UpcomingMatch = {
  id: string;
  team_id: string;
  league_configuration_id: string;
  opponent_name: string | null;
  match_date: string;
  match_time: string | null;
  warmup_time?: string | null;
  court_numbers?: string | null;
  opponent_average_edr?: number | null;
  match_site_type: string | null;
  facility_id: string | null;
  match_status: string;
  lineup_status: string;
  occurrence_state?: string;
  result_completeness?: string;
  match_results?: {
    result_status: string;
    team_result_value: string;
    our_team_score: number;
    opponent_team_score: number;
  }[] | null;
};

type UpcomingEventsViewState = {
  rangeDays: number;
};

type BirthdaySnoozeViewState = {
  snoozedUntilByKey: Record<string, string>;
};

type CaptainFeedMenuState = {
  anchorEl: HTMLElement;
  itemId: string;
} | null;

type TeamEventAttendanceRow = {
  event_id: string;
  rsvp_status: string | null;
  match_status: string | null;
  players?: {
    sex?: string | null;
  } | Array<{
    sex?: string | null;
  }> | null;
};

type TeamEventAttendanceSummary = {
  goingTotal: number;
  goingMen: number;
  goingWomen: number;
};

type UpcomingEventRow = {
  id: string;
  kind: 'match' | 'team-event';
  teamId: string;
  title: string;
  teamName: string;
  eventDate: string;
  startTime: string | null;
  endTime: string | null;
  location: string;
  eventType?: TeamEventRecord['event_type'];
  attendance?: TeamEventAttendanceSummary;
  onOpen: () => void;
};

type AvailabilitySummary = {
  matchesWithUnknown: number;
  matchesWithLowAvailableMaybe: number;
};

type MatchAvailabilityCounts = {
  available: number;
  maybe: number;
  unknown: number;
  notAvailable: number;
};

type FeesSummary = {
  outstanding: number;
  overdue: number;
  playersOwing: number;
  overdueFees: number;
  unpaidItems: number;
  dueSoon: number;
  dueSoonPlayers: number;
};

type AvailabilitySummaryRecord = {
  match_event_id: string;
  player_id: string;
  availability_status: string;
};

type RequiredSlotsRow = {
  id: string;
  league_configuration_id: string;
  position_code: string;
  display_order: number;
  required_player_count: number;
};

type LineupEntryRow = {
  match_event_id: string;
  league_configuration_position_id: string;
  slot_number: number;
  player_id: string;
};

type LineupPositionStatusRow = {
  match_event_id: string;
  league_configuration_position_id: string;
  line_status: 'normal' | 'default_by_opponent' | 'default_by_us';
};

type PlayerRow = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  birthday?: string | null;
  estimated_dynamic_rating?: number | null;
};

type FeeAssignmentRow = {
  fee_assignment_id: string;
  fee_campaign_id: string;
  team_id: string | null;
  player_id: string;
  remaining_balance: number;
  computed_status: string;
};

type FeeCampaignRow = {
  id: string;
  due_date: string | null;
};

type LineupLineSummary = {
  positionCode: string;
  text: string;
};

type LineupSummary = {
  lines: LineupLineSummary[];
  warnings: {
    incomplete: boolean;
    duplicates: boolean;
    notAvailable: boolean;
    defaulted: boolean;
    maybeUnknown: boolean;
    unknownAssigned: boolean;
  };
};

type ActionSeverity = 'critical' | 'warning' | 'info';

type ActionAlert = {
  id: string;
  severity: ActionSeverity;
  type: string;
  team?: string;
  match?: string;
  player?: string;
  detail: string;
  dueDate: string;
  actionLabel: string;
  onAction: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
};

type ParticipationAlert = {
  id: string;
  severity: ActionSeverity;
  title: string;
  teamName: string;
  players: string[];
  actionLabel: string;
  onAction: () => void;
};

type BirthdayAlert = {
  id: string;
  playerName: string;
  birthdayLabel: string;
  occurrence: string;
};

type RegistrationAlert = {
  id: string;
  rosterId: string;
  playerName: string;
  teamId: string;
  teamName: string;
  registrationStatus: string;
};

type ReminderType = 'home_match_welcome_message' | 'away_match_follow_up';

type CommandCenterReminderRow = {
  id: string;
  reminder_type: ReminderType;
  match_event_id: string;
  team_id: string;
  dismissed: boolean;
};

type InformationalReminder = {
  id: string;
  reminderType: ReminderType;
  match: UpcomingMatch;
  teamName: string;
  title: string;
  matchType: string;
  detail: string;
};

type AlertReminderItem = {
  id: string;
  kind: 'reminder' | 'participation' | 'birthday' | 'registration';
  severity: ActionSeverity;
  title: string;
  teamName?: string;
  primary: string;
  secondary: string;
  sortDate: string;
  actionLabel?: string;
  onAction?: () => void;
  copyLabel?: string;
  onCopy?: () => void;
  onDismiss: () => void;
};

type TeamSnapshot = {
  teamId: string;
  teamName: string;
  shortName: string | null;
  teamColor: string | null;
  iconUrl: string | null;
  organizationId: string | null;
  seasonLabel: string | null;
  leagueName: string | null;
  record: string;
  remainingMatches: number;
  nextMatch?: UpcomingMatch;
  availability?: MatchAvailabilityCounts;
};

type TeamRow = {
  id: string;
  team_name: string;
  short_name: string | null;
  team_color: string | null;
  team_icon_url: string | null;
  organization_id: string | null;
  season_label: string | null;
  league_name: string | null;
  is_active: boolean;
};

type ParticipationAlertTeamRow = {
  id: string;
};

type TeamDateRangeRow = { team_id: string; first_match_date: string | null; last_match_date: string | null };

type RegistrationRosterRow = {
  id: string;
  registration_status: string | null;
  players?: PlayerRow | null;
  teams?: {
    id: string;
    team_name: string;
    is_active: boolean;
  } | null;
};

type NotificationLogRow = {
  id: string;
  created_at: string;
  notification_type: string;
  category: string;
  severity: string;
  title: string;
  team_id: string | null;
  player_id: string | null;
  match_event_id: string | null;
  fee_campaign_id: string | null;
  fee_assignment_id: string | null;
  dismissed: boolean;
  resolved: boolean;
};

type NotificationLogMatchRow = {
  id: string;
  opponent_name: string | null;
  match_date: string;
  match_time: string | null;
};

function lineStatusLabel(lineStatus: LineupPositionStatusRow['line_status']): string {
  if (lineStatus === 'default_by_opponent') return 'Default by Opponent';
  if (lineStatus === 'default_by_us') return 'Default by Us';
  return 'Normal';
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(dateIso: string, days: number): string {
  const date = new Date(`${dateIso}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function isIsoDateOnly(value: unknown): value is string {
  return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function isBirthdayNotification(notification: CommandCenterNotification): boolean {
  return notification.notificationType === 'upcoming_birthday';
}

function notificationFeature(notification: { notificationType: string; category?: string | null; matchEventId?: string | null }): TeamPermissionFeature | 'owner_only' | null {
  if (notification.notificationType === 'participation_review_needed') return 'matches_lineups';
  if (notification.notificationType === 'season_provider_player_change') return 'roster_management';
  if (notification.notificationType === 'welcome_email_bounced') return 'roster_management';
  if (notification.notificationType === 'season_provider_time_change' || notification.notificationType === 'season_provider_new_match') return 'matches_lineups';
  if (notification.notificationType === 'team_event_details_confirmation') return 'team_events';
  if (notification.category === 'Outstanding Fees' || notification.notificationType.startsWith('payment_')) return 'fees_payments';
  if (notification.notificationType === 'missing_availability') return 'player_availability';
  if (['players_below_target', 'player_not_played_recently', 'players_above_target'].includes(notification.notificationType)) return 'reports';
  if (['upcoming_birthday', 'player_registration_needed'].includes(notification.notificationType)) return 'roster_management';
  if (notification.notificationType === 'tennisrecord_edr_sync_due') return 'owner_only';
  if (
    notification.matchEventId ||
    [
      'lineup_not_started',
      'lineup_not_published',
      'home_match_welcome_message',
      'tcd_scoresheet_print_reminder',
      'away_match_follow_up',
      'match_scores_required',
      'match_scores_partial',
      'results_confirmed_not_submitted',
    ].includes(notification.notificationType)
  ) return 'matches_lineups';
  return null;
}

function canShowNotification(notification: CommandCenterNotification, featureAccess: CommandCenterFeatureAccess, isOwner: boolean): boolean {
  const feature = notificationFeature(notification);
  if (feature === 'owner_only') return isOwner;
  if (!feature) return true;
  return Boolean(featureAccess[feature]);
}

function canShowNotificationLogRow(row: NotificationLogRow, featureAccess: CommandCenterFeatureAccess, isOwner: boolean): boolean {
  const feature = notificationFeature({
    notificationType: row.notification_type,
    category: row.category,
    matchEventId: row.match_event_id,
  });
  if (feature === 'owner_only') return isOwner;
  if (!feature) return true;
  return Boolean(featureAccess[feature]);
}

function daysBetween(fromIso: string, toIso: string): number {
  const from = new Date(`${fromIso}T00:00:00`).getTime();
  const to = new Date(`${toIso}T00:00:00`).getTime();
  return Math.round((to - from) / 86_400_000);
}

function formatDate(value: string): string {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function formatEventDateParts(value: string): { monthDay: string; weekday: string } {
  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return {
    monthDay: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }).toUpperCase(),
    weekday: date.toLocaleDateString(undefined, { weekday: 'short' }),
  };
}

function formatTimeRange(startTime: string | null, endTime: string | null, timeFormat: TimeFormat): string {
  const start = formatTime(startTime, timeFormat);
  const end = formatTime(endTime, timeFormat);
  if (start && end) return `${start} – ${end}`;
  return start || end || '';
}

function compareEventStartTimes(left: string | null, right: string | null): number {
  if (left && right) return left.localeCompare(right);
  if (left) return -1;
  if (right) return 1;
  return 0;
}

function urgencyText(matchDate: string): string {
  const delta = daysBetween(todayIso(), matchDate);
  if (delta === 0) return 'Today';
  if (delta > 0) return `Match in ${delta} day${delta === 1 ? '' : 's'}`;
  const elapsed = Math.abs(delta);
  return `Match was ${elapsed} day${elapsed === 1 ? '' : 's'} ago`;
}

function resultStatus(match: UpcomingMatch): string {
  return match.match_results?.[0]?.result_status || 'Not Started';
}

function severityRank(severity: ActionSeverity): number {
  if (severity === 'critical') return 0;
  if (severity === 'warning') return 1;
  return 2;
}

function severityColor(severity: ActionSeverity): 'error' | 'warning' | 'info' {
  if (severity === 'critical') return 'error';
  if (severity === 'warning') return 'warning';
  return 'info';
}

function notificationSeverityColor(severity: NotificationSeverity): 'error' | 'warning' | 'info' {
  if (severity === 'Critical') return 'error';
  if (severity === 'Warning') return 'warning';
  return 'info';
}

function notificationSeverityBg(severity: NotificationSeverity): string {
  if (severity === 'Critical') return 'rgba(211, 47, 47, 0.10)';
  if (severity === 'Warning') return 'rgba(237, 108, 2, 0.10)';
  return 'rgba(21, 101, 192, 0.08)';
}

function notificationSeverityLabel(severity: NotificationSeverity): string {
  if (severity === 'Critical') return 'Action Required';
  if (severity === 'Warning') return 'Warning';
  return 'Info';
}

function money(value: number): string {
  return `$${value.toFixed(2)}`;
}

function availabilityText(counts?: MatchAvailabilityCounts): string {
  if (!counts) return 'Availability not initialized';
  return `${counts.available} Available, ${counts.maybe} Maybe, ${counts.unknown} Unknown, ${counts.notAvailable} Unavailable`;
}

function attendanceSummary(rows: TeamEventAttendanceRow[]): TeamEventAttendanceSummary {
  const summary = summarizeGroupMeOnlyRsvps(rows.map((row) => ({
    latest_rsvp_status: row.rsvp_status,
    match_status: row.match_status,
    players: Array.isArray(row.players) ? row.players[0] : row.players,
  })));
  return {
    goingTotal: summary.goingTotal,
    goingMen: summary.goingMen,
    goingWomen: summary.goingWomen,
  };
}

function eventIconStyle(row: UpcomingEventRow): { color: string; bgcolor: string } {
  if (row.kind === 'match') return { color: '#1565c0', bgcolor: '#e3f2fd' };
  if (row.eventType === 'Practice') return { color: '#2e7d32', bgcolor: 'rgba(46, 125, 50, 0.10)' };
  if (row.eventType === 'Social') return { color: '#c2185b', bgcolor: '#fce4ec' };
  return { color: '#ed6c02', bgcolor: 'rgba(237, 108, 2, 0.10)' };
}

function eventIcon(row: UpcomingEventRow) {
  if (row.kind === 'match') return <SportsTennisIcon fontSize="small" />;
  if (row.eventType === 'Practice') return <GroupsIcon fontSize="small" />;
  if (row.eventType === 'Social') return <CelebrationIcon fontSize="small" />;
  return <EventIcon fontSize="small" />;
}

function notificationLogMatchLabel(match: NotificationLogMatchRow | undefined, timeFormat: TimeFormat): string {
  if (!match) return '';
  const opponent = match.opponent_name || 'TBD';
  const time = match.match_time ? ` ${formatTime(match.match_time, timeFormat, match)}` : '';
  return `${formatDate(localEventDate(match))}${time} vs ${opponent}`;
}

function notificationLogContext(row: NotificationLogRow, lookups: LookupState, matchesById: Record<string, NotificationLogMatchRow>, timeFormat: TimeFormat): string {
  const values = [
    row.team_id ? `Team: ${lookupLabel(lookups, 'teams', row.team_id)}` : '',
    row.player_id ? `Player: ${lookupLabel(lookups, 'players', row.player_id)}` : '',
    row.match_event_id ? `Match: ${notificationLogMatchLabel(matchesById[row.match_event_id], timeFormat) || 'Match details unavailable'}` : '',
    row.fee_campaign_id ? `Fee Campaign: ${row.fee_campaign_id.slice(0, 8)}` : '',
    row.fee_assignment_id ? `Fee Assignment: ${row.fee_assignment_id.slice(0, 8)}` : '',
  ].filter(Boolean);
  return values.join(' • ') || 'General';
}

function greeting(): string {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
}

function firstName(value: string): string {
  return value.trim().split(/\s+/)[0] || 'Captain';
}

function birthdayOccurrenceThisYear(birthday: string, baseYear: number): string {
  const [, month, day] = birthday.split('-');
  return `${baseYear}-${month}-${day}`;
}

function reminderTitle(type: ReminderType): string {
  if (type === 'home_match_welcome_message') return 'Welcome Message Reminder';
  return 'Away Match Follow-Up';
}

function reminderSortLabel(type: ReminderType): string {
  if (type === 'home_match_welcome_message') return 'Home Match Welcome Message';
  return 'Away Match Follow-Up';
}

function loadDismissedInfoKeys(): string[] {
  try {
    return JSON.parse(window.localStorage.getItem('commandCenterDismissedInfoKeys') || '[]') as string[];
  } catch {
    return [];
  }
}

function saveDismissedInfoKeys(keys: string[]): void {
  window.localStorage.setItem('commandCenterDismissedInfoKeys', JSON.stringify(keys));
}

export function CommandCenter({
  lookups,
  onOpenMatch,
  onOpenMatchScores,
  onOpenLineup,
  onOpenOutstandingFees,
  onOpenPaymentInbox,
  onOpenParticipationReport,
  onOpenPlayers,
  onOpenTeams,
  onOpenTeamDetail,
  onOpenAvailability,
  onOpenTeamRoster,
  onOpenCommunications,
  onOpenMatches,
  onOpenTeamEvents,
  onOpenTeamEvent,
  onOpenNotificationSettings,
  onOpenTennisRecordSync,
  captainName,
  timeFormat,
  userPreferences,
  writableTeamIds,
  isOwner,
  featureAccess,
}: CommandCenterProps) {
  const auditUserId = useAuditUserId();
  const { teamAssignments } = useAuth();
  const [matches, setMatches] = useState<UpcomingMatch[]>([]);
  const [upcomingEventMatches, setUpcomingEventMatches] = useState<UpcomingMatch[]>([]);
  const [teamEvents, setTeamEvents] = useState<TeamEventRecord[]>([]);
  const [teamEventAttendance, setTeamEventAttendance] = useState<Record<string, TeamEventAttendanceSummary>>({});
  const [lineupSummaries, setLineupSummaries] = useState<Record<string, LineupSummary>>({});
  const [availabilityByMatch, setAvailabilityByMatch] = useState<Record<string, MatchAvailabilityCounts>>({});
  const [actionAlerts, setActionAlerts] = useState<ActionAlert[]>([]);
  const [participationAlerts, setParticipationAlerts] = useState<ParticipationAlert[]>([]);
  const [informationalReminders, setInformationalReminders] = useState<InformationalReminder[]>([]);
  const [birthdayAlerts, setBirthdayAlerts] = useState<BirthdayAlert[]>([]);
  const [registrationAlerts, setRegistrationAlerts] = useState<RegistrationAlert[]>([]);
  const [notifications, setNotifications] = useState<CommandCenterNotification[]>([]);
  const [teamSnapshots, setTeamSnapshots] = useState<TeamSnapshot[]>([]);
  const [availabilitySummary, setAvailabilitySummary] = useState<AvailabilitySummary>({ matchesWithUnknown: 0, matchesWithLowAvailableMaybe: 0 });
  const [feesSummary, setFeesSummary] = useState<FeesSummary>({ outstanding: 0, overdue: 0, playersOwing: 0, overdueFees: 0, unpaidItems: 0, dueSoon: 0, dueSoonPlayers: 0 });
  const [dismissedInfoKeys, setDismissedInfoKeys] = useState<string[]>(() => loadDismissedInfoKeys());
  const [notificationLogOpen, setNotificationLogOpen] = useState(false);
  const [notificationLogRows, setNotificationLogRows] = useState<NotificationLogRow[]>([]);
  const [notificationLogMatchesById, setNotificationLogMatchesById] = useState<Record<string, NotificationLogMatchRow>>({});
  const [notificationLogLoading, setNotificationLogLoading] = useState(false);
  const [captainFeedMenu, setCaptainFeedMenu] = useState<CaptainFeedMenuState>(null);
  const [eventReview, setEventReview] = useState<{ notification: CommandCenterNotification; event: TeamEventRecord } | null>(null);
  const [eventReviewBusy, setEventReviewBusy] = useState(false);
  const [seasonSyncReview, setSeasonSyncReview] = useState<CommandCenterNotification | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);
  const { state: upcomingEventsViewState, setState: setUpcomingEventsViewState } = usePersistentViewState<UpcomingEventsViewState>({
    userId: auditUserId,
    routeKey: 'command-center:upcoming-events',
    defaultState: { rangeDays: 7 },
    validate: (state): state is UpcomingEventsViewState => Boolean(
      state &&
      typeof state === 'object' &&
      'rangeDays' in state &&
      [7, 14, 30, 60, 90].includes(Number((state as UpcomingEventsViewState).rangeDays)),
    ),
  });
  const { state: birthdaySnoozeViewState, setState: setBirthdaySnoozeViewState } = usePersistentViewState<BirthdaySnoozeViewState>({
    userId: auditUserId,
    routeKey: 'command-center:birthday-snoozes',
    defaultState: { snoozedUntilByKey: {} },
    validate: (state): state is BirthdaySnoozeViewState => Boolean(
      state &&
      typeof state === 'object' &&
      'snoozedUntilByKey' in state &&
      (state as BirthdaySnoozeViewState).snoozedUntilByKey !== null &&
      typeof (state as BirthdaySnoozeViewState).snoozedUntilByKey === 'object' &&
      !Array.isArray((state as BirthdaySnoozeViewState).snoozedUntilByKey) &&
      Object.values((state as BirthdaySnoozeViewState).snoozedUntilByKey).every(isIsoDateOnly),
    ),
  });
  const upcomingEventsRangeDays = upcomingEventsViewState.rangeDays;
  const birthdaySnoozedUntilByKey = birthdaySnoozeViewState.snoozedUntilByKey;
  const activeTeamIds = useMemo(() => lookups.teams.filter((team) => team.is_active !== false).map((team) => team.value), [lookups.teams]);
  const captainTeamIds = useMemo(
    () => responsibleCommandCenterTeamIds(teamAssignments, activeTeamIds),
    [activeTeamIds, teamAssignments],
  );
  const captainTeamIdSet = useMemo(() => new Set(captainTeamIds || []), [captainTeamIds]);
  const captainLookups = useMemo(() => ({
    ...lookups,
    teams: lookups.teams.filter((team) => captainTeamIdSet.has(team.value)),
  }), [captainTeamIdSet, lookups]);
  const canViewMatches = featureAccess.matches_lineups;
  const canViewTeamEvents = featureAccess.team_events;
  const canViewAvailability = featureAccess.player_availability;
  const canViewFees = featureAccess.fees_payments;
  const canViewCommunications = featureAccess.communications;
  const canViewReports = featureAccess.reports;
  const canViewRoster = featureAccess.roster_management;
  const canViewUpcomingEvents = canViewMatches || canViewTeamEvents;

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'command-center', ready: !loading });

  const dismissInfoItem = (key: string) => {
    setDismissedInfoKeys((current) => {
      const next = [...new Set([...current, key])];
      saveDismissedInfoKeys(next);
      return next;
    });
    setSnackMessage('Alert dismissed.');
  };

  const loadNotifications = useCallback(async () => {
    try {
      const nextNotifications = await loadCommandCenterNotifications({
        lookups: captainLookups,
        participationReviewLookups: isOwner ? lookups : undefined,
        timeFormat,
        userPreferences,
        writableTeamIds: writableTeamIds.filter((teamId) => captainTeamIdSet.has(teamId)),
        isOwner: false,
        currentUserId: auditUserId,
      });
      setNotifications(nextNotifications.filter((notification) => canShowNotification(notification, featureAccess, isOwner)));
      setError(null);
    } catch (notificationError) {
      setError(notificationError instanceof Error ? notificationError.message : 'Unable to load notifications.');
    }
  }, [captainLookups, captainTeamIdSet, featureAccess, isOwner, lookups, timeFormat, userPreferences, writableTeamIds]);

  const loadNotificationLog = useCallback(async () => {
    setNotificationLogLoading(true);
    if (captainTeamIds.length === 0) {
      setNotificationLogRows([]);
      setNotificationLogMatchesById({});
      setNotificationLogLoading(false);
      return;
    }
    const { data, error: logError } = await supabase
      .from('notification_instances')
      .select('id, created_at, notification_type, category, severity, title, team_id, player_id, match_event_id, fee_campaign_id, fee_assignment_id, dismissed, resolved')
      .order('created_at', { ascending: false })
      .in('team_id', captainTeamIds)
      .limit(100);
    if (logError) {
      setNotificationLogLoading(false);
      setError(logError.message);
      return;
    }
    const rows = ((data || []) as unknown as NotificationLogRow[])
      .filter((row) => captainTeamIdSet.has(row.team_id || ''))
      .filter((row) => canShowNotificationLogRow(row, featureAccess, isOwner));
    const matchIds = Array.from(new Set(rows.map((row) => row.match_event_id).filter((matchId): matchId is string => Boolean(matchId))));
    if (matchIds.length > 0) {
      const { data: matchData, error: matchError } = await supabase
        .from('match_events')
        .select('id, opponent_name, match_date, timezone, match_time')
        .in('id', matchIds)
        .is('deleted_at', null);
      if (matchError) {
        setNotificationLogLoading(false);
        setError(matchError.message);
        return;
      }
      const matchesById = ((matchData || []) as unknown as NotificationLogMatchRow[]).reduce<Record<string, NotificationLogMatchRow>>((acc, match) => {
        acc[match.id] = match;
        return acc;
      }, {});
      setNotificationLogMatchesById(matchesById);
    } else {
      setNotificationLogMatchesById({});
    }
    setNotificationLogRows(rows);
    setNotificationLogLoading(false);
  }, [captainTeamIdSet, captainTeamIds, featureAccess, isOwner]);

  const openNotificationLog = () => {
    setNotificationLogOpen(true);
    void loadNotificationLog();
  };

  const loadUpcomingMatches = useCallback(async () => {
    if (captainTeamIds.length === 0 || !canViewUpcomingEvents) {
      setMatches([]);
      setUpcomingEventMatches([]);
      setTeamEvents([]);
      setTeamEventAttendance({});
      setLineupSummaries({});
      setAvailabilityByMatch({});
      setActionAlerts([]);
      setInformationalReminders([]);
      setAvailabilitySummary({ matchesWithUnknown: 0, matchesWithLowAvailableMaybe: 0 });
      return;
    }
    setLoading(true);
    const today = todayIso();
    const earliestResultAlertDate = addDays(today, -21);
    const latestDashboardDate = addDays(today, 30);
    const latestEventDate = addDays(today, upcomingEventsRangeDays);
    const latestMatchQueryDate = addDays(today, Math.max(30, upcomingEventsRangeDays));
    const matchPromise = canViewMatches
      ? supabase
        .from('match_events')
        .select('id, team_id, league_configuration_id, opponent_name, match_date, timezone, match_time, warmup_time, court_numbers, opponent_average_edr, match_site_type, facility_id, match_status, lineup_status, match_results(result_status, team_result_value, our_team_score, opponent_team_score)')
        .is('deleted_at', null)
        .in('team_id', captainTeamIds)
        .gte('match_date', earliestResultAlertDate)
        .lte('match_date', addDays(latestMatchQueryDate, 1))
        .neq('match_status', 'Cancelled')
        .order('match_date', { ascending: true })
        .order('match_time', { ascending: true })
        .limit(120)
      : Promise.resolve({ data: [], error: null });
    const teamEventsPromise = canViewTeamEvents
      ? supabase
        .from('events')
        .select('*')
        .is('deleted_at', null)
        .neq('event_type', 'Match')
        .gte('event_date', addDays(today, -1))
        .lte('event_date', addDays(latestEventDate, 1))
        .neq('status', 'Cancelled')
        .neq('status', 'Completed')
        .order('event_date', { ascending: true })
        .order('start_time', { ascending: true })
        .limit(120)
      : Promise.resolve({ data: [], error: null });
    const [{ data, error: loadError }, teamEventsResult] = await Promise.all([matchPromise, teamEventsPromise]);
    setLoading(false);

    if (loadError) {
      setError(loadError.message);
      return;
    }
    if (teamEventsResult.error) {
      setError(teamEventsResult.error.message);
      setTeamEvents([]);
      setTeamEventAttendance({});
    } else {
      const loadedTeamEvents = ((teamEventsResult.data || []) as unknown as TeamEventRecord[]).filter((event) => localEventDate(event) >= today && localEventDate(event) <= latestEventDate);
      setTeamEvents(loadedTeamEvents);
      const eventIds = loadedTeamEvents.map((event) => event.id);
      if (eventIds.length === 0) {
        setTeamEventAttendance({});
      } else {
        const attendanceResult = await supabase
          .from('event_attendance')
          .select('event_id, rsvp_status, match_status, players(sex)')
          .in('event_id', eventIds)
          .is('deleted_at', null)
          .limit(3000);
        if (attendanceResult.error) {
          setTeamEventAttendance({});
        } else {
          const rowsByEventId = ((attendanceResult.data || []) as unknown as TeamEventAttendanceRow[]).reduce<Record<string, TeamEventAttendanceRow[]>>((acc, row) => {
            acc[row.event_id] ||= [];
            acc[row.event_id].push(row);
            return acc;
          }, {});
          setTeamEventAttendance(Object.fromEntries(Object.entries(rowsByEventId).map(([eventId, rows]) => [eventId, attendanceSummary(rows)])));
        }
      }
    }

    const allRows = (data || []) as unknown as UpcomingMatch[];
    if (allRows.length > 0) {
      const occurrenceResult = await supabase.rpc('match_occurrence_states', { p_match_ids: allRows.map((match) => match.id) });
      if (occurrenceResult.error) {
        setError(occurrenceResult.error.message);
        return;
      }
      const occurrenceById = new Map(((occurrenceResult.data || []) as Array<{
        match_event_id: string;
        occurrence_state: string;
        result_completeness: string;
      }>).map((row) => [row.match_event_id, row]));
      for (const match of allRows) {
        const occurrence = occurrenceById.get(match.id);
        match.occurrence_state = occurrence?.occurrence_state;
        match.result_completeness = occurrence?.result_completeness;
      }
    }
    const upcomingRows = canViewMatches ? allRows.filter((match) => localEventDate(match) >= today && localEventDate(match) <= latestDashboardDate && match.match_status !== 'Completed') : [];
    setUpcomingEventMatches(canViewMatches ? allRows.filter((match) => localEventDate(match) >= today && localEventDate(match) <= latestEventDate && match.match_status !== 'Completed') : []);
    if (upcomingRows.length > 0) {
      const initResults = await Promise.all(upcomingRows.map((match) => supabase.rpc('initialize_availability_for_match', { p_match_event_id: match.id })));
      const initError = initResults.find((result) => result.error)?.error;
      if (initError) {
        setError(initError.message);
        setMatches(upcomingRows.slice(0, 12));
        return;
      }
    }

    const matchIds = allRows.map((match) => match.id);
    const leagueConfigurationIds = Array.from(new Set(allRows.map((match) => match.league_configuration_id)));
    const [availabilityResult, positionsResult, entriesResult, lineStatusResult] = await Promise.all([
      matchIds.length > 0
        ? supabase.from('availability_records').select('match_event_id, player_id, availability_status').in('match_event_id', matchIds).is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      leagueConfigurationIds.length > 0
        ? supabase
            .from('league_configuration_positions')
            .select('id, league_configuration_id, position_code, display_order, required_player_count')
            .in('league_configuration_id', leagueConfigurationIds)
            .is('deleted_at', null)
            .eq('is_active', true)
            .order('display_order', { ascending: true })
        : Promise.resolve({ data: [], error: null }),
      matchIds.length > 0
        ? supabase
            .from('lineup_entries')
            .select('match_event_id, league_configuration_position_id, slot_number, player_id')
            .in('match_event_id', matchIds)
            .is('deleted_at', null)
        : Promise.resolve({ data: [], error: null }),
      matchIds.length > 0
        ? supabase.from('lineup_position_statuses').select('match_event_id, league_configuration_position_id, line_status').in('match_event_id', matchIds)
        : Promise.resolve({ data: [], error: null }),
    ]);

    if (availabilityResult.error || positionsResult.error || entriesResult.error || lineStatusResult.error) {
      setError(
        availabilityResult.error?.message ||
          positionsResult.error?.message ||
          entriesResult.error?.message ||
          lineStatusResult.error?.message ||
          'Unable to load dashboard lineups.',
      );
      setMatches(upcomingRows.slice(0, 12));
      return;
    }

    const requiredSlotsByConfig = ((positionsResult.data || []) as unknown as RequiredSlotsRow[]).reduce<Record<string, number>>((acc, row) => {
      acc[row.league_configuration_id] = (acc[row.league_configuration_id] || 0) + Number(row.required_player_count || 0);
      return acc;
    }, {});
    const availabilityRows = (availabilityResult.data || []) as unknown as AvailabilitySummaryRecord[];
    const availabilityCountsByMatch = ((availabilityResult.data || []) as unknown as AvailabilitySummaryRecord[]).reduce<
      Record<string, MatchAvailabilityCounts>
    >((acc, row) => {
      acc[row.match_event_id] ||= { available: 0, maybe: 0, unknown: 0, notAvailable: 0 };
      if (row.availability_status === 'available') acc[row.match_event_id].available += 1;
      if (row.availability_status === 'maybe') acc[row.match_event_id].maybe += 1;
      if (row.availability_status === 'unknown') acc[row.match_event_id].unknown += 1;
      if (row.availability_status === 'not_available') acc[row.match_event_id].notAvailable += 1;
      return acc;
    }, {});

    const entries = (entriesResult.data || []) as unknown as LineupEntryRow[];
    const playerIds = Array.from(new Set(entries.map((entry) => entry.player_id)));
    const playersResult =
      playerIds.length > 0
        ? await supabase.from('players').select('id, first_name, last_name, estimated_dynamic_rating').in('id', playerIds).is('deleted_at', null)
        : { data: [], error: null };

    if (playersResult.error) {
      setError(playersResult.error.message);
      setMatches(upcomingRows.slice(0, 12));
      return;
    }

    const positions = (positionsResult.data || []) as unknown as RequiredSlotsRow[];
    const lineStatuses = (lineStatusResult.data || []) as unknown as LineupPositionStatusRow[];
    const playersById = ((playersResult.data || []) as unknown as PlayerRow[]).reduce<Record<string, PlayerRow>>((acc, player) => {
      acc[player.id] = player;
      return acc;
    }, {});
    const availabilityByPlayer = availabilityRows.reduce<Record<string, string>>((acc, row) => {
      acc[`${row.match_event_id}:${row.player_id}`] = row.availability_status;
      return acc;
    }, {});

    const positionsByConfig = positions.reduce<Record<string, RequiredSlotsRow[]>>((acc, position) => {
      acc[position.league_configuration_id] ||= [];
      acc[position.league_configuration_id].push(position);
      return acc;
    }, {});
    Object.values(positionsByConfig).forEach((group) => group.sort((a, b) => a.display_order - b.display_order || a.position_code.localeCompare(b.position_code)));
    const entriesByMatchPosition = entries.reduce<Record<string, LineupEntryRow[]>>((acc, entry) => {
      acc[`${entry.match_event_id}:${entry.league_configuration_position_id}`] ||= [];
      acc[`${entry.match_event_id}:${entry.league_configuration_position_id}`].push(entry);
      return acc;
    }, {});
    const lineStatusByMatchPosition = lineStatuses.reduce<Record<string, LineupPositionStatusRow['line_status']>>((acc, row) => {
      acc[`${row.match_event_id}:${row.league_configuration_position_id}`] = row.line_status;
      return acc;
    }, {});
    const positionsById = positions.reduce<Record<string, RequiredSlotsRow>>((acc, position) => {
      acc[position.id] = position;
      return acc;
    }, {});
    const summaries: Record<string, LineupSummary> = {};
    allRows.forEach((match) => {
      const matchPositions = positionsByConfig[match.league_configuration_id] || [];
      const allEntries = entries.filter((entry) => entry.match_event_id === match.id);
      const lineStatusRows = lineStatuses.filter((lineStatus) => lineStatus.match_event_id === match.id);
      const hasStarted = match.lineup_status !== 'Not Started' || allEntries.length > 0 || lineStatusRows.some((lineStatus) => lineStatus.line_status !== 'normal');

      if (!hasStarted) {
        summaries[match.id] = {
          lines: [{ positionCode: '', text: 'Lineup Not Started' }],
          warnings: { incomplete: false, duplicates: false, notAvailable: false, defaulted: false, maybeUnknown: false, unknownAssigned: false },
        };
      } else {
        const assignedPlayerCounts = allEntries.reduce<Record<string, number>>((acc, entry) => {
          acc[entry.player_id] = (acc[entry.player_id] || 0) + 1;
          return acc;
        }, {});
        const warnings = {
          incomplete: false,
          duplicates: Object.values(assignedPlayerCounts).some((count) => count > 1),
          notAvailable: false,
          defaulted: false,
          maybeUnknown: false,
          unknownAssigned: false,
        };
        const lines = matchPositions.map((position) => {
          const lineStatus = lineStatusByMatchPosition[`${match.id}:${position.id}`] || 'normal';
          if (lineStatus !== 'normal') {
            warnings.defaulted = true;
            return { positionCode: position.position_code, text: `${position.position_code}: ${lineStatusLabel(lineStatus)}` };
          }
          const positionEntries = (entriesByMatchPosition[`${match.id}:${position.id}`] || []).sort((a, b) => a.slot_number - b.slot_number);
          positionEntries.forEach((entry) => {
            const status = availabilityByPlayer[`${match.id}:${entry.player_id}`];
            if (status === 'not_available') warnings.notAvailable = true;
            if (status === 'maybe' || status === 'unknown') warnings.maybeUnknown = true;
            if (status === 'unknown') warnings.unknownAssigned = true;
          });
          const names = Array.from({ length: position.required_player_count }, (_, index) => {
            const entry = positionEntries.find((candidate) => candidate.slot_number === index + 1);
            return entry ? formatPlayerName(playersById[entry.player_id], userPreferences) || 'TBD' : 'TBD';
          });
          if (positionEntries.length === 0) {
            warnings.incomplete = true;
            return { positionCode: position.position_code, text: `${position.position_code}: Not Assigned` };
          }
          if (positionEntries.length < position.required_player_count || names.includes('TBD')) {
            warnings.incomplete = true;
            return { positionCode: position.position_code, text: `${position.position_code}: Partially Assigned: ${names.join(' / ')}` };
          }
          return { positionCode: position.position_code, text: `${position.position_code}: ${names.join(' / ')}` };
        });
        summaries[match.id] = { lines, warnings };
      }
    });

    const nextActionAlerts: ActionAlert[] = [];
    upcomingRows.forEach((match) => {
      const daysUntilMatch = daysBetween(today, match.match_date);
      const team = lookupLabel(lookups, 'teams', match.team_id);
      const matchLabel = `vs ${match.opponent_name || 'TBD'}`;
      if (daysUntilMatch <= 14 && match.lineup_status === 'Not Started') {
        nextActionAlerts.push({
          id: `lineup-not-started-${match.id}`,
          severity: 'critical',
          type: 'Lineup Not Started',
          team,
          match: matchLabel,
          detail: urgencyText(match.match_date),
          dueDate: match.match_date,
          actionLabel: 'Open Match Lineup',
          onAction: () => onOpenLineup(match.id),
        });
      }
      if (daysUntilMatch <= 7 && match.lineup_status !== 'Published') {
        nextActionAlerts.push({
          id: `lineup-not-published-${match.id}`,
          severity: 'critical',
          type: 'Lineup Not Published',
          team,
          match: matchLabel,
          detail: urgencyText(match.match_date),
          dueDate: match.match_date,
          actionLabel: 'Open Match Lineup',
          onAction: () => onOpenLineup(match.id),
        });
      }
    });

    if (canViewAvailability) entries.forEach((entry) => {
      const match = upcomingRows.find((candidate) => candidate.id === entry.match_event_id);
      if (!match || daysBetween(today, match.match_date) > 10) return;
      if (availabilityByPlayer[`${entry.match_event_id}:${entry.player_id}`] !== 'maybe') return;
      nextActionAlerts.push({
        id: `assigned-maybe-${match.id}-${entry.player_id}-${entry.league_configuration_position_id}`,
        severity: 'warning',
        type: 'Assigned Player Availability Uncertain',
        team: lookupLabel(lookups, 'teams', match.team_id),
        match: `vs ${match.opponent_name || 'TBD'} • ${formatDate(localEventDate(match))}${formatTime(match.match_time, timeFormat, match) ? ` ${formatTime(match.match_time, timeFormat, match)}` : ''}`,
        player: formatPlayerName(playersById[entry.player_id], userPreferences) || lookupLabel(lookups, 'players', entry.player_id),
        detail: `Assigned: ${positionsById[entry.league_configuration_position_id]?.position_code || 'Line'} • Availability: Maybe • ${urgencyText(match.match_date)}`,
        dueDate: match.match_date,
        actionLabel: 'Open Availability',
        onAction: () => onOpenAvailability(match.team_id),
        secondaryActionLabel: 'Open Lineup',
        onSecondaryAction: () => onOpenLineup(match.id),
      });
    });

    allRows
      .filter((match) => match.occurrence_state === 'Past' && match.match_status !== 'Completed')
      .forEach((match) => {
        const status = resultStatus(match);
        if (!['Not Started', 'Draft'].includes(status)) return;
        const partial = match.result_completeness === 'Partial Results';
        const complete = match.result_completeness === 'Results Complete';
        nextActionAlerts.push({
          id: `results-${status}-${match.id}`,
          severity: 'critical',
          type: complete ? 'Results Awaiting Confirmation' : partial ? 'Enter Missing Scores' : 'Results Not Entered',
          team: lookupLabel(lookups, 'teams', match.team_id),
          match: `vs ${match.opponent_name || 'TBD'}`,
          detail: complete ? 'Line results entered but not confirmed' : partial ? 'Some line results are missing' : urgencyText(match.match_date),
          dueDate: match.match_date,
          actionLabel: 'Open Results',
          onAction: () => onOpenMatch(match.id),
        });
      });

    allRows
      .filter((match) => match.match_status === 'Scores Entered' && resultStatus(match) === 'Confirmed')
      .forEach((match) => {
        nextActionAlerts.push({
          id: `results-confirmed-${match.id}`,
          severity: 'warning',
          type: 'Results Ready for Submission',
          team: lookupLabel(lookups, 'teams', match.team_id),
          match: `vs ${match.opponent_name || 'TBD'}`,
          detail: 'Results confirmed but not submitted',
          dueDate: match.match_date,
          actionLabel: 'Open Results',
          onAction: () => onOpenMatch(match.id),
        });
      });

    nextActionAlerts.sort((a, b) => severityRank(a.severity) - severityRank(b.severity) || a.dueDate.localeCompare(b.dueDate));

    const reminderCandidates = canViewCommunications ? upcomingRows.flatMap((match) => {
      const daysUntilMatch = daysBetween(today, match.match_date);
      const candidates: InformationalReminder[] = [];
      if (match.match_site_type === 'Home' && daysUntilMatch <= 7) {
        candidates.push({
          id: `home_match_welcome_message:${match.id}`,
          reminderType: 'home_match_welcome_message',
          match,
          teamName: lookupLabel(lookups, 'teams', match.team_id),
          title: reminderTitle('home_match_welcome_message'),
          matchType: 'Home Match',
          detail: 'You typically send a welcome message to the opposing captain about one week before home matches.',
        });
      }
      if (match.match_site_type === 'Away' && daysUntilMatch <= 4) {
        candidates.push({
          id: `away_match_follow_up:${match.id}`,
          reminderType: 'away_match_follow_up',
          match,
          teamName: lookupLabel(lookups, 'teams', match.team_id),
          title: reminderTitle('away_match_follow_up'),
          matchType: 'Away Match',
          detail: 'Have you heard from the opposing captain yet?',
        });
      }
      return candidates;
    }) : [];
    let dismissedReminderKeys = new Set<string>();
    if (reminderCandidates.length > 0) {
      const reminderResult = await supabase
        .from('command_center_reminders')
        .select('id, reminder_type, match_event_id, team_id, dismissed')
        .in('match_event_id', [...new Set(reminderCandidates.map((reminder) => reminder.match.id))])
        .is('deleted_at', null);
      if (reminderResult.error) {
        if (!reminderResult.error.message.includes('command_center_reminders')) setError(reminderResult.error.message);
      } else {
        dismissedReminderKeys = new Set(
          ((reminderResult.data || []) as unknown as CommandCenterReminderRow[])
            .filter((reminder) => reminder.dismissed)
            .map((reminder) => `${reminder.reminder_type}:${reminder.match_event_id}`),
        );
      }
    }
    setInformationalReminders(
      reminderCandidates
        .filter((reminder) => !dismissedReminderKeys.has(`${reminder.reminderType}:${reminder.match.id}`))
        .sort((a, b) => a.match.match_date.localeCompare(b.match.match_date) || a.teamName.localeCompare(b.teamName) || reminderSortLabel(a.reminderType).localeCompare(reminderSortLabel(b.reminderType))),
    );

    setAvailabilitySummary(canViewAvailability ? {
      matchesWithUnknown: upcomingRows.filter((match) => (availabilityCountsByMatch[match.id]?.unknown || 0) > 0).length,
      matchesWithLowAvailableMaybe: upcomingRows.filter((match) => {
        const requiredSlots = requiredSlotsByConfig[match.league_configuration_id] || 0;
        if (requiredSlots === 0) return false;
        const counts = availabilityCountsByMatch[match.id];
        return ((counts?.available || 0) + (counts?.maybe || 0)) < requiredSlots;
      }).length,
    } : { matchesWithUnknown: 0, matchesWithLowAvailableMaybe: 0 });
    setError(null);
    setMatches(upcomingRows.slice(0, 12));
    setLineupSummaries(summaries);
    setAvailabilityByMatch(availabilityCountsByMatch);
    setActionAlerts(canViewMatches ? nextActionAlerts : []);
  }, [captainTeamIds, canViewAvailability, canViewCommunications, canViewMatches, canViewTeamEvents, canViewUpcomingEvents, lookups, onOpenAvailability, onOpenLineup, onOpenMatch, timeFormat, upcomingEventsRangeDays, userPreferences]);

  const loadFeesSummary = useCallback(async () => {
    if (!featureAccess.fees_payments) {
      setFeesSummary({ outstanding: 0, overdue: 0, playersOwing: 0, overdueFees: 0, unpaidItems: 0, dueSoon: 0, dueSoonPlayers: 0 });
      return;
    }
    if (captainTeamIds.length === 0) {
      setFeesSummary({ outstanding: 0, overdue: 0, playersOwing: 0, overdueFees: 0, unpaidItems: 0, dueSoon: 0, dueSoonPlayers: 0 });
      return;
    }
    const [assignmentResult, campaignResult] = await Promise.all([
      supabase
        .from('player_fee_assignment_financials')
        .select('fee_assignment_id, fee_campaign_id, team_id, player_id, remaining_balance, computed_status')
        .in('team_id', captainTeamIds),
      supabase.from('team_fee_campaigns').select('id, due_date').is('deleted_at', null),
    ]);

    if (assignmentResult.error || campaignResult.error) return;

    const campaignsById = ((campaignResult.data || []) as unknown as FeeCampaignRow[]).reduce<Record<string, FeeCampaignRow>>((acc, campaign) => {
      acc[campaign.id] = campaign;
      return acc;
    }, {});
    const current = new Date(new Date().toISOString().slice(0, 10));
    const today = todayIso();
    const soonDate = addDays(today, 7);
    const players = new Set<string>();
    const dueSoonPlayers = new Set<string>();
    let outstanding = 0;
    let overdue = 0;
    let overdueFees = 0;
    let unpaidItems = 0;
    let dueSoon = 0;

    ((assignmentResult.data || []) as unknown as FeeAssignmentRow[]).forEach((assignment) => {
      if (assignment.computed_status === 'Paid' || assignment.computed_status === 'Waived') return;
      const balance = Math.max(0, Number(assignment.remaining_balance || 0));
      if (balance <= 0) return;
      outstanding += balance;
      unpaidItems += 1;
      players.add(assignment.player_id);
      const dueDate = campaignsById[assignment.fee_campaign_id]?.due_date;
      if (dueDate && new Date(`${dueDate}T00:00:00`) < current) {
        overdue += balance;
        overdueFees += 1;
      }
      if (dueDate && dueDate >= today && dueDate <= soonDate) {
        dueSoon += balance;
        dueSoonPlayers.add(assignment.player_id);
      }
    });

    setFeesSummary({ outstanding, overdue, playersOwing: players.size, overdueFees, unpaidItems, dueSoon, dueSoonPlayers: dueSoonPlayers.size });
  }, [captainTeamIds, featureAccess.fees_payments]);

  const loadParticipationAlerts = useCallback(async () => {
    if (!featureAccess.reports || captainTeamIds.length === 0) {
      setParticipationAlerts([]);
      return;
    }
    const today = todayIso();
    const [{ data: teamData, error: teamError }, { data: dateRanges, error: dateRangeError }] = await Promise.all([
      supabase.from('teams').select('id').eq('is_active', true).in('id', captainTeamIds).is('deleted_at', null),
      supabase.from('team_playing_date_range').select('team_id, first_match_date, last_match_date').in('team_id', captainTeamIds),
    ]);
    if (teamError || dateRangeError) return;
    const ranges = new Map(((dateRanges || []) as TeamDateRangeRow[]).map((row) => [row.team_id, row]));
    const activeTeams = ((teamData || []) as unknown as ParticipationAlertTeamRow[])
      .filter((team) => {
        const firstMatchDate = ranges.get(team.id)?.first_match_date;
        return !firstMatchDate || firstMatchDate <= today;
      })
      .slice(0, 8);
    const alerts: ParticipationAlert[] = [];
    await Promise.all(
      activeTeams.map(async (team) => {
        try {
          const { rows } = await loadParticipationReportRows({
            teamId: team.id,
            includeInactivePlayers: false,
            includeSubstitutePlayers: false,
            userPreferences,
            playerLabel: (playerId) => lookupLabel(lookups, 'players', playerId),
          });
          const below: string[] = [];
          const above: string[] = [];
          rows.forEach((row) => {
            if (row.statusDelta <= -2 && below.length < 5) below.push(`${row.playerName} (${row.statusDelta})`);
            if (row.statusDelta > 0 && above.length < 5) above.push(`${row.playerName} (+${row.statusDelta})`);
          });
          const teamName = lookupLabel(lookups, 'teams', team.id);
          if (below.length > 0) {
            alerts.push({
              id: `players-below-target-${team.id}`,
              severity: 'warning',
              title: 'Players Below Target',
              teamName,
              players: below,
              actionLabel: 'Open Participation Report',
              onAction: () => onOpenParticipationReport(team.id),
            });
          }
          if (above.length > 0) {
            alerts.push({
              id: `players-above-target-${team.id}`,
              severity: 'info',
              title: 'Players Above Target',
              teamName,
              players: above,
              actionLabel: 'Open Participation Report',
              onAction: () => onOpenParticipationReport(team.id),
            });
          }
        } catch {
          // Keep the Command Center responsive if one team's participation report is not ready.
        }
      }),
    );
    setParticipationAlerts(alerts.sort((a, b) => severityRank(a.severity) - severityRank(b.severity) || a.teamName.localeCompare(b.teamName)).slice(0, 8));
  }, [captainTeamIds, captainTeamIdSet, featureAccess.reports, lookups, onOpenParticipationReport, userPreferences]);

  const loadBirthdayAlerts = useCallback(async () => {
    // Birthday reminders are supplied by the scoped notification engine below.
    // Keeping this legacy local list empty avoids a second, workspace-wide query.
    setBirthdayAlerts([]);
  }, []);

  const loadRegistrationAlerts = useCallback(async () => {
    if (!featureAccess.roster_management || captainTeamIds.length === 0) {
      setRegistrationAlerts([]);
      return;
    }
    const today = todayIso();
    const [{ data, error: registrationError }, { data: dateRanges, error: dateRangeError }] = await Promise.all([
      supabase.from('team_rosters')
        .select('id, registration_status, players!inner(id, first_name, last_name), teams!inner(id, team_name, is_active)')
        .eq('is_active', true)
        .is('deleted_at', null)
        .in('team_id', captainTeamIds)
        .in('registration_status', ['Unknown', 'Not Registered'])
        .eq('teams.is_active', true)
        .is('teams.deleted_at', null)
        .is('players.deleted_at', null)
        .order('created_at', { ascending: true })
        .limit(24),
      supabase.from('team_playing_date_range').select('team_id, first_match_date, last_match_date').in('team_id', captainTeamIds),
    ]);
    if (registrationError || dateRangeError) return;
    const ranges = new Map(((dateRanges || []) as TeamDateRangeRow[]).map((row) => [row.team_id, row]));
    const alerts = ((data || []) as unknown as RegistrationRosterRow[])
      .filter((row) => {
        const range = ranges.get(row.teams?.id || '');
        if (range?.first_match_date && range.first_match_date > addDays(today, 21)) return false;
        return !range?.last_match_date || range.last_match_date >= today;
      })
      .filter((row) => row.players && row.teams)
      .map<RegistrationAlert>((row) => ({
        id: `registration-${row.id}`,
        rosterId: row.id,
        playerName: formatPlayerName(row.players || undefined, userPreferences) || lookupLabel(lookups, 'players', row.players?.id),
        teamId: row.teams!.id,
        teamName: row.teams!.team_name,
        registrationStatus: row.registration_status || 'Unknown',
      }));
    setRegistrationAlerts(alerts);
  }, [captainTeamIds, featureAccess.roster_management, lookups, userPreferences]);

  const loadTeamSnapshots = useCallback(async () => {
    if (captainTeamIds.length === 0) {
      setTeamSnapshots([]);
      return;
    }
    const [{ data: teamsData, error: teamsError }, { data: matchesData, error: matchesError }] = await Promise.all([
      supabase.from('teams').select('id, team_name, short_name, team_color, team_icon_url, organization_id, season_label, league_name, is_active').eq('is_active', true).is('deleted_at', null).in('id', captainTeamIds).order('team_name'),
      supabase
        .from('match_events')
        .select('id, team_id, league_configuration_id, opponent_name, match_date, timezone, match_time, opponent_average_edr, match_site_type, facility_id, match_status, lineup_status, match_results(result_status, team_result_value, our_team_score, opponent_team_score)')
        .is('deleted_at', null)
        .neq('match_status', 'Cancelled')
        .in('team_id', captainTeamIds)
        .order('match_date', { ascending: true }),
    ]);
    if (teamsError || matchesError) return;
    const teams = (teamsData || []) as unknown as TeamRow[];
    const allMatches = (matchesData || []) as unknown as UpcomingMatch[];
    const today = todayIso();
    const snapshots = teams.map<TeamSnapshot>((team) => {
      const teamMatches = allMatches.filter((match) => match.team_id === team.id);
      const completed = teamMatches.filter((match) => match.match_status === 'Completed');
      const wins = completed.filter((match) => match.match_results?.[0]?.team_result_value === 'Win').length;
      const losses = completed.filter((match) => match.match_results?.[0]?.team_result_value === 'Loss').length;
      const remainingMatches = teamMatches.filter((match) => match.match_date >= today && match.match_status !== 'Completed').length;
      const nextMatch = teamMatches.find((match) => match.match_date >= today && match.match_status !== 'Completed');
      return {
        teamId: team.id,
        teamName: team.team_name,
        shortName: team.short_name,
        teamColor: team.team_color,
        iconUrl: team.team_icon_url,
        organizationId: team.organization_id,
        seasonLabel: team.season_label,
        leagueName: team.league_name,
        record: `${wins}-${losses}`,
        remainingMatches,
        nextMatch,
        availability: nextMatch ? availabilityByMatch[nextMatch.id] : undefined,
      };
    });
    setTeamSnapshots(snapshots);
  }, [availabilityByMatch, captainTeamIds]);

  const dismissReminder = async (reminder: InformationalReminder, message = 'Reminder dismissed.') => {
    const now = new Date().toISOString();
    const { data: existing, error: findError } = await supabase
      .from('command_center_reminders')
      .select('id')
      .eq('reminder_type', reminder.reminderType)
      .eq('match_event_id', reminder.match.id)
      .is('deleted_at', null)
      .maybeSingle();
    if (findError) {
      setError(findError.message);
      return;
    }
    const payload = {
      reminder_type: reminder.reminderType,
      match_event_id: reminder.match.id,
      team_id: reminder.match.team_id,
      dismissed: true,
      dismissed_by: auditUserId,
      dismissed_at: now,
      updated_by: auditUserId,
    };
    const result = existing
      ? await supabase.from('command_center_reminders').update(payload).eq('id', (existing as { id: string }).id)
      : await supabase.from('command_center_reminders').insert({ ...payload, created_by: auditUserId });
    if (result.error) {
      setError(result.error.message);
      return;
    }
    setInformationalReminders((current) => current.filter((item) => item.id !== reminder.id));
    setSnackMessage(message);
  };

  const copyWelcomeReminder = async (reminder: InformationalReminder) => {
    const { data: teamData, error: teamError } = await supabase
      .from('teams')
      .select('id, team_name, league_name, home_facility_id, home_match_opponent_message_template')
      .eq('id', reminder.match.team_id)
      .is('deleted_at', null)
      .maybeSingle();
    if (teamError) {
      setError(teamError.message);
      return;
    }
    try {
      const { message } = await buildCaptainWelcomeMessage(reminder.match as unknown as MatchEventRecord, (teamData || null) as CommunicationTeam | null, timeFormat);
      await navigator.clipboard.writeText(message);
      setSnackMessage('Welcome message copied. Resolve the reminder after sending it.');
    } catch (copyError) {
      setError(copyError instanceof Error ? copyError.message : 'Unable to copy welcome message.');
    }
  };

  const markRegistrationAlertRegistered = async (alert: RegistrationAlert) => {
    const { error: registrationError } = await supabase
      .from('team_rosters')
      .update({
        registration_status: 'Registered',
        registration_verified_at: new Date().toISOString(),
        registration_verified_by: auditUserId,
      })
      .eq('id', alert.rosterId);
    if (registrationError) {
      setError(registrationError.message);
      return;
    }
    setRegistrationAlerts((current) => current.filter((item) => item.rosterId !== alert.rosterId));
    setSnackMessage(`${alert.playerName} marked registered for ${alert.teamName}.`);
  };

  const openNotificationRoute = (route?: string) => {
    if (!route) return;
    const [kind, id] = route.split(':');
    if (kind === 'lineup' && id && canViewMatches) onOpenLineup(id);
    if (kind === 'match' && id && canViewMatches) onOpenMatch(id);
    if (kind === 'scores' && id && canViewMatches) onOpenMatchScores(id);
    if (kind === 'availability' && canViewAvailability) onOpenAvailability(id || null);
    if (kind === 'payment-inbox' && canViewFees) onOpenPaymentInbox();
    if (kind === 'participation-report' && canViewReports) onOpenParticipationReport(id || null);
    if (kind === 'team-roster' && canViewRoster) onOpenTeamRoster(id || null);
    if (kind === 'players' && canViewRoster) onOpenPlayers();
    if (kind === 'tennisrecord-sync' && isOwner) onOpenTennisRecordSync();
    if (kind === 'team-event-review' && id && canViewTeamEvents) {
      const notification = notifications.find((item) => item.teamEventId === id && item.notificationType === 'team_event_details_confirmation');
      if (notification) void reviewTeamEvent(notification);
    }
    if (kind === 'season-review' && id && (canViewMatches || canViewRoster)) {
      const notification = notifications.find((item) => item.seasonReview?.id === id);
      if (notification) setSeasonSyncReview(notification);
    }
  };

  const reviewTeamEvent = async (notification: CommandCenterNotification) => {
    if (!canViewTeamEvents || !notification.teamEventId) return;
    setEventReviewBusy(true);
    try {
      const { data, error: loadError } = await supabase.from('events')
        .select('id, team_id, event_type, title, event_date, start_time, timezone, facility_id, location_text, status')
        .eq('id', notification.teamEventId).is('deleted_at', null).maybeSingle();
      if (loadError) throw new Error(loadError.message);
      if (!data || !isConfirmableTeamEvent(data as TeamEventRecord)) {
        setSnackMessage('This event is no longer scheduled. Refreshing reminders.');
        void loadNotifications();
        return;
      }
      const event = data as TeamEventRecord;
      if (teamEventConfirmationKey(event) !== notification.key) {
        setSnackMessage('Event details changed. Refreshing the reminder.');
        void loadNotifications();
        return;
      }
      setEventReview({ notification, event });
    } catch (reviewError) {
      setError(reviewError instanceof Error ? reviewError.message : 'Unable to load event details.');
    } finally {
      setEventReviewBusy(false);
    }
  };

  const confirmTeamEventDetails = async () => {
    if (!eventReview || !canViewTeamEvents) return;
    setEventReviewBusy(true);
    try {
      const { data, error: loadError } = await supabase.from('events')
        .select('id, event_type, event_date, start_time, timezone, facility_id, location_text, status')
        .eq('id', eventReview.event.id).is('deleted_at', null).maybeSingle();
      if (loadError) throw new Error(loadError.message);
      if (!data || teamEventConfirmationKey(data as TeamEventRecord) !== eventReview.notification.key) {
        setEventReview(null);
        setSnackMessage('Event details changed. Please review them again.');
        await loadNotifications();
        return;
      }
      if (!data.start_time || (!data.facility_id && !data.location_text?.trim())) {
        setError('Set both the event time and location before confirming.');
        return;
      }
      await dismissCommandCenterNotification(eventReview.notification, auditUserId);
      setNotifications((current) => current.filter((item) => item.key !== eventReview.notification.key));
      setEventReview(null);
      setSnackMessage('Event time and location confirmed.');
    } catch (confirmError) {
      setError(confirmError instanceof Error ? confirmError.message : 'Unable to confirm event details.');
    } finally {
      setEventReviewBusy(false);
    }
  };

  const dismissNotification = async (notification: CommandCenterNotification, message = 'Alert dismissed.') => {
    try {
      await dismissCommandCenterNotification(notification, auditUserId);
      setNotifications((current) => current.filter((item) => item.key !== notification.key));
      setSnackMessage(message);
    } catch (dismissError) {
      setError(dismissError instanceof Error ? dismissError.message : 'Unable to dismiss alert.');
    }
  };

  const snoozeProviderScoreNotification = async (notification: CommandCenterNotification) => {
    if (!auditUserId || notification.notificationType !== 'official_provider_score_disagreement') return;
    try {
      const days = notification.snoozeDays || 3;
      await setProviderScoreWarningDisposition(notification.key, auditUserId, days);
      setNotifications((current) => current.filter((item) => item.key !== notification.key));
      setSnackMessage(`Provider score warning snoozed for ${days} days.`);
      closeCaptainFeedMenu();
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'Unable to snooze warning.'); }
  };

  const closeCaptainFeedMenu = () => {
    setCaptainFeedMenu(null);
  };

  const snoozeBirthdayNotification = (notification: CommandCenterNotification, snoozedUntil: string, message: string) => {
    setBirthdaySnoozeViewState({
      snoozedUntilByKey: {
        ...birthdaySnoozedUntilByKey,
        [notification.key]: snoozedUntil,
      },
    });
    setSnackMessage(message);
    closeCaptainFeedMenu();
  };

  const copyWelcomeNotification = async (notification: CommandCenterNotification) => {
    if (!notification.matchEventId) return;
    const [{ data: matchData, error: matchError }, { data: teamData, error: teamError }] = await Promise.all([
      supabase
        .from('match_events')
        .select('id, team_id, league_configuration_id, match_classification, match_date, timezone, match_time, warmup_time, opponent_name, opponent_captain_names, opponent_average_edr, facility_id, court_numbers, match_site_type, match_status, lineup_status, lifecycle_state, lineup_published_at, lineup_published_by, lineup_locked_at, lineup_locked_by, completed_at, completed_by, notes_summary, groupme_event_id, groupme_event_title')
        .eq('id', notification.matchEventId)
        .is('deleted_at', null)
        .maybeSingle(),
      supabase
        .from('teams')
        .select('id, team_name, league_name, home_facility_id, home_match_opponent_message_template')
        .eq('id', notification.teamId)
        .is('deleted_at', null)
        .maybeSingle(),
    ]);
    if (matchError || teamError) {
      setError(matchError?.message || teamError?.message || 'Unable to build welcome message.');
      return;
    }
    try {
      const { message } = await buildCaptainWelcomeMessage(matchData as MatchEventRecord, (teamData || null) as CommunicationTeam | null, timeFormat);
      await navigator.clipboard.writeText(message);
      setSnackMessage('Welcome message copied. Resolve the reminder after sending it.');
    } catch (copyError) {
      setError(copyError instanceof Error ? copyError.message : 'Unable to copy welcome message.');
    }
  };

  const markRegistrationNotificationRegistered = async (notification: CommandCenterNotification) => {
    if (!notification.rosterId) return;
    const { error: registrationError } = await supabase
      .from('team_rosters')
      .update({
        registration_status: 'Registered',
        registration_verified_at: new Date().toISOString(),
        registration_verified_by: auditUserId,
      })
      .eq('id', notification.rosterId);
    if (registrationError) {
      setError(registrationError.message);
      return;
    }
    setNotifications((current) => current.filter((item) => item.key !== notification.key));
    setSnackMessage(`${notification.playerName || 'Player'} marked registered for ${notification.teamName || 'team'}.`);
  };

  const runNotificationAction = async (notification: CommandCenterNotification, action = notification.primaryAction) => {
    if (!action) return;
    if (notification.notificationType === 'participation_review_needed' && notification.matchEventId) {
      window.sessionStorage.setItem('courtcaptain:participation-review-target', notification.matchEventId);
    }
    if (action.kind === 'copy_welcome') {
      await copyWelcomeNotification(notification);
      return;
    }
    if (action.kind === 'mark_registered') {
      await markRegistrationNotificationRegistered(notification);
      return;
    }
    if (action.kind === 'run_tennisrecord_sync') {
      window.sessionStorage.setItem('courtcaptain:run-tennisrecord-sync', '1');
      onOpenTennisRecordSync();
      setSnackMessage('Opening TennisRecord Sync and starting the browser sync.');
      return;
    }
    openNotificationRoute(action.route);
  };

  useEffect(() => {
    void loadUpcomingMatches();
    void loadNotifications();
    void loadFeesSummary();
    void loadParticipationAlerts();
    void loadBirthdayAlerts();
    void loadRegistrationAlerts();
    void loadNotificationLog();
  }, [captainTeamIds, loadBirthdayAlerts, loadFeesSummary, loadNotificationLog, loadNotifications, loadParticipationAlerts, loadRegistrationAlerts, loadUpcomingMatches]);

  useEffect(() => {
    void loadTeamSnapshots();
  }, [captainTeamIds, loadTeamSnapshots]);

  const alertReminderItems = useMemo<AlertReminderItem[]>(() => {
    const reminderItems = canViewCommunications ? informationalReminders.map<AlertReminderItem>((reminder) => ({
      id: reminder.id,
      kind: 'reminder',
      severity: 'info',
      title: reminder.title,
      teamName: reminder.teamName,
      primary: `vs ${reminder.match.opponent_name || 'TBD'} • ${formatDate(localEventDate(reminder.match))}${formatTime(reminder.match.match_time, timeFormat, reminder.match) ? ` ${formatTime(reminder.match.match_time, timeFormat, reminder.match)}` : ''}`,
      secondary: `${reminder.matchType} • ${urgencyText(reminder.match.match_date)} • ${reminder.detail}`,
      sortDate: reminder.match.match_date,
      copyLabel: reminder.reminderType === 'home_match_welcome_message' ? 'Copy Welcome Message' : undefined,
      onCopy: reminder.reminderType === 'home_match_welcome_message' ? () => void copyWelcomeReminder(reminder) : undefined,
      onDismiss: () => void dismissReminder(reminder),
    })) : [];
    const participationItems = canViewReports ? participationAlerts
      .map<AlertReminderItem>((alert) => ({
        id: alert.id,
        kind: 'participation',
        severity: alert.severity,
        title: alert.title,
        teamName: alert.teamName,
        primary: alert.players.join(', '),
        secondary: 'Participation target reminder',
        sortDate: todayIso(),
        actionLabel: alert.actionLabel,
        onAction: alert.onAction,
        onDismiss: () => dismissInfoItem(alert.id),
      }))
      .filter((item) => !dismissedInfoKeys.includes(item.id)) : [];
    const birthdayItems = canViewRoster ? birthdayAlerts
      .map<AlertReminderItem>((birthday) => {
        const id = `birthday-${birthday.id}-${birthday.occurrence}`;
        return {
          id,
          kind: 'birthday',
          severity: 'info',
          title: 'Upcoming Birthday',
          primary: `${birthday.playerName} • ${birthday.birthdayLabel}`,
          secondary: 'Player birthday in the next 14 days',
          sortDate: birthday.occurrence,
          actionLabel: 'Open Player Record',
          onAction: onOpenPlayers,
          onDismiss: () => dismissInfoItem(id),
        };
      })
      .filter((item) => !dismissedInfoKeys.includes(item.id)) : [];
    const registrationItems = canViewRoster ? registrationAlerts
      .map<AlertReminderItem>((alert) => ({
        id: alert.id,
        kind: 'registration',
        severity: 'info',
        title: 'Player Registration Needed',
        teamName: alert.teamName,
        primary: `${alert.playerName} has not been verified as registered for ${alert.teamName}.`,
        secondary: `Registration Status: ${alert.registrationStatus}`,
        sortDate: todayIso(),
        actionLabel: 'Open Team Roster',
        onAction: () => onOpenTeamRoster(alert.teamId),
        copyLabel: 'Mark Registered',
        onCopy: () => void markRegistrationAlertRegistered(alert),
        onDismiss: () => dismissInfoItem(alert.id),
      }))
      .filter((item) => !dismissedInfoKeys.includes(item.id)) : [];
    return [...reminderItems, ...participationItems, ...birthdayItems, ...registrationItems].sort(
      (a, b) => severityRank(a.severity) - severityRank(b.severity) || a.sortDate.localeCompare(b.sortDate) || a.title.localeCompare(b.title),
    );
  }, [birthdayAlerts, canViewCommunications, canViewReports, canViewRoster, dismissedInfoKeys, informationalReminders, onOpenPlayers, onOpenTeamRoster, participationAlerts, registrationAlerts, timeFormat]);

  const actionNotifications = useMemo(
    () => notifications.filter((notification) => notification.category === 'Action Required'),
    [notifications],
  );

  const alertReminderNotifications = useMemo(
    () => notifications.filter((notification) => notification.category === 'Alerts & Reminders'),
    [notifications],
  );

  const visibleAlertReminderNotifications = useMemo(
    () => alertReminderNotifications.filter((notification) => {
      if (!isBirthdayNotification(notification)) return true;
      const snoozedUntil = birthdaySnoozedUntilByKey[notification.key];
      return !snoozedUntil || snoozedUntil <= todayIso();
    }),
    [alertReminderNotifications, birthdaySnoozedUntilByKey],
  );

  const featuredMatch = matches[0] || null;
  const upcomingEventRows = useMemo<UpcomingEventRow[]>(() => {
    const matchRows = canViewMatches ? upcomingEventMatches.map<UpcomingEventRow>((match) => ({
      id: match.id,
      kind: 'match',
      teamId: match.team_id,
      title: `${match.match_site_type === 'Away' ? '@' : 'vs'} ${match.opponent_name || 'TBD'}${match.match_site_type && match.match_site_type !== 'Unknown' ? ` (${match.match_site_type.charAt(0)})` : ''}`,
      teamName: lookupLabel(lookups, 'teams', match.team_id),
      eventDate: localEventDate(match),
      startTime: localEventParts(match).time || null,
      endTime: null,
      location: lookupLabel(lookups, 'facilities', match.facility_id) || match.court_numbers || '',
      onOpen: () => onOpenMatch(match.id),
    })) : [];
    const teamEventRows = canViewTeamEvents ? teamEvents.map<UpcomingEventRow>((event) => ({
      id: event.id,
      kind: 'team-event',
      teamId: event.team_id,
      title: event.title,
      teamName: event.audience_scope === 'workspace' ? 'Workspace Event' : lookupLabel(lookups, 'teams', event.team_id),
      eventDate: localEventDate(event),
      startTime: localEventParts(event).time || null,
      endTime: localEventParts(event, event.end_time).time || null,
      location: lookupLabel(lookups, 'facilities', event.facility_id) || event.location_text || '',
      eventType: event.event_type,
      attendance: teamEventAttendance[event.id] || { goingTotal: 0, goingMen: 0, goingWomen: 0 },
      onOpen: () => onOpenTeamEvent(event.id),
    })) : [];
    return [...matchRows, ...teamEventRows]
      .sort((a, b) => a.eventDate.localeCompare(b.eventDate) || compareEventStartTimes(a.startTime, b.startTime) || a.title.localeCompare(b.title) || a.id.localeCompare(b.id));
  }, [canViewMatches, canViewTeamEvents, lookups, onOpenMatch, onOpenTeamEvent, teamEventAttendance, teamEvents, upcomingEventMatches]);
  const recentActivityRows = notificationLogRows.slice(0, 10);

  const refreshDashboard = () => {
    void loadUpcomingMatches();
    void loadNotifications();
    void loadFeesSummary();
    void loadNotificationLog();
  };

  const sortTime = (value?: string | null) => {
    if (!value) return 0;
    const timestamp = Date.parse(value.includes('T') ? value : `${value}T00:00:00`);
    return Number.isNaN(timestamp) ? 0 : timestamp;
  };

  const captainFeedCandidates = [
    ...actionNotifications.map((notification) => ({
      id: notification.key,
      notification,
      isBirthday: false,
      priority: 0,
      severity: notification.severity,
      label: notificationSeverityLabel(notification.severity),
      title: notification.title,
      message: notification.message,
      sortDate: notification.sortDate || notification.dueDate || todayIso(),
      timestamp: notification.dueDate ? formatDate(notification.dueDate) : 'Today',
      actionLabel: notification.primaryAction?.label || 'Open',
      onAction: () => void runNotificationAction(notification),
      dismissible: false,
      onDismiss: undefined,
      icon: <WarningAmberIcon fontSize="small" />,
    })),
    ...visibleAlertReminderNotifications.map((notification) => {
      const birthday = isBirthdayNotification(notification);
      return {
      id: notification.key,
      notification,
      isBirthday: birthday,
      priority: 1,
      severity: notification.severity,
      label: notificationSeverityLabel(notification.severity),
      title: notification.title,
      message: notification.message,
      sortDate: notification.sortDate || notification.dueDate || todayIso(),
      timestamp: notification.dueDate ? formatDate(notification.dueDate) : 'Today',
      actionLabel: notification.primaryAction?.label || 'Dismiss',
      onAction: () => notification.primaryAction ? void runNotificationAction(notification) : void dismissNotification(notification),
      dismissible: notification.dismissible,
      onDismiss: notification.dismissible ? () => void dismissNotification(notification) : undefined,
      icon: birthday ? <CakeIcon fontSize="small" /> : <NotificationsIcon fontSize="small" />,
      };
    }),
  ];
  const captainFeedItems = Array.from(
    captainFeedCandidates.reduce((itemsById, item) => {
      if (!itemsById.has(item.id)) itemsById.set(item.id, item);
      return itemsById;
    }, new Map<string, (typeof captainFeedCandidates)[number]>()),
    ([, item]) => item,
  ).sort((a, b) => a.priority - b.priority || sortTime(b.sortDate) - sortTime(a.sortDate) || a.title.localeCompare(b.title));
  const selectedCaptainFeedItem = captainFeedItems.find((item) => item.id === captainFeedMenu?.itemId) || null;
  const selectedBirthdaySnoozeBaseDate = selectedCaptainFeedItem?.notification.dueDate || selectedCaptainFeedItem?.notification.sortDate || todayIso();

  const quickActions = [
    ...(canViewFees ? [{ label: 'Record Payment', icon: <PaymentsIcon />, onClick: onOpenOutstandingFees }] : []),
    ...(canViewMatches ? [{ label: 'Add Match', icon: <EventIcon />, onClick: onOpenMatches }] : []),
    ...(canViewTeamEvents ? [{ label: 'Add Event', icon: <CalendarMonthIcon />, onClick: onOpenTeamEvents }] : []),
    ...(canViewRoster ? [{ label: 'Add Player', icon: <PersonAddIcon />, onClick: onOpenPlayers }] : []),
  ];

  if (captainTeamIds.length === 0) {
    return (
      <Stack spacing={2.25}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, fontSize: { xs: 26, md: 30 } }}>{greeting()}, {firstName(captainName)}</Typography>
          <Typography variant="body1" color="text.secondary">Here&apos;s what&apos;s happening with your teams today.</Typography>
        </Box>
        {error && <Alert severity="error">{error}</Alert>}
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>No captain teams assigned</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            You do not currently have any teams assigned to you as Captain or Co-Captain.
          </Typography>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.25}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} sx={{ pt: 0.5 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900, fontSize: { xs: 26, md: 30 }, letterSpacing: 0 }}>
            {greeting()}, {firstName(captainName)}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Here's what's happening with your teams today.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
          <Button size="small" variant="outlined" startIcon={<SettingsIcon />} onClick={onOpenNotificationSettings} sx={{ fontWeight: 800 }}>
            Customize Dashboard
          </Button>
          <Tooltip title="Notification log">
            <IconButton color="primary" onClick={openNotificationLog} aria-label="Notification log">
              <NotificationsIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh dashboard">
            <IconButton color="primary" onClick={refreshDashboard} aria-label="Refresh dashboard">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {loading && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ maxWidth: 930 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 900 }}>My Teams</Typography>
          {teamSnapshots.length > 4 && <Button size="small" onClick={onOpenTeams}>View All Teams</Button>}
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
          {teamSnapshots.slice(0, 4).map((team) => (
            <Paper key={team.teamId} component="button" type="button" variant="outlined" onClick={() => onOpenTeamDetail(team.teamId)}
              sx={{ p: 1.75, textAlign: 'left', cursor: 'pointer', bgcolor: '#fff', borderLeft: `5px solid ${lookupTeamAccentColor(lookups, team.teamId)}`, '&:hover': { bgcolor: 'action.hover' } }}>
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                <TeamIcon name={team.shortName || team.teamName} color={team.teamColor} url={team.iconUrl} size={42} />
                <Typography variant="subtitle1" sx={{ fontWeight: 850, lineHeight: 1.2 }}>{team.shortName || team.teamName}</Typography>
              </Stack>
              <Typography variant="body2"><strong>{team.record}</strong> Record · <strong>{team.remainingMatches}</strong> Remaining</Typography>
              {team.nextMatch ? (
                <Box sx={{ mt: 0.75 }}>
                  <Typography variant="body2" sx={{ fontWeight: 750 }}>Next: {formatDate(localEventDate(team.nextMatch))} · {team.nextMatch.opponent_name || 'TBD'}</Typography>
                  <Typography variant="caption" color="text.secondary">{[formatTime(team.nextMatch.match_time, timeFormat, team.nextMatch), lookupLabel(lookups, 'facilities', team.nextMatch.facility_id)].filter(Boolean).join(' · ')}</Typography>
                </Box>
              ) : <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>No upcoming match</Typography>}
            </Paper>
          ))}
          {teamSnapshots.length === 0 && <Typography variant="body2" color="text.secondary">No active captain teams found.</Typography>}
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 0.95fr) minmax(520px, 1.05fr)' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Captain Feed</Typography>
                {actionNotifications.length > 0 && <Chip size="small" label={`${actionNotifications.length} Action Required`} color="error" sx={{ fontWeight: 800 }} />}
                {visibleAlertReminderNotifications.length > 0 && <Chip size="small" label={`${visibleAlertReminderNotifications.length} Notifications`} color="warning" sx={{ fontWeight: 800 }} />}
              </Stack>
              <Button size="small" onClick={openNotificationLog}>View All</Button>
            </Stack>
            <Stack spacing={0} sx={{ maxHeight: 430, overflow: 'auto', pr: 0.75 }}>
              {captainFeedItems.map((item) => (
              <Box key={item.id} sx={{ py: 1.35, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ minWidth: 0 }}>
                    <Box sx={{ width: 42, height: 42, borderRadius: '50%', display: 'grid', placeItems: 'center', color: `${notificationSeverityColor(item.severity)}.main`, bgcolor: notificationSeverityBg(item.severity), flexShrink: 0 }}>
                      {item.icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color={`${notificationSeverityColor(item.severity)}.main`} sx={{ fontWeight: 900, textTransform: 'uppercase' }}>{item.label}</Typography>
                      <Typography variant="subtitle2" sx={{ fontWeight: 850 }}>{item.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{item.message}</Typography>
                      {item.notification.notificationType === 'home_match_welcome_message' && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Opposing Captains: {opposingCaptainNames(item.notification.opposingCaptainNames).join(', ') || 'Not provided'}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent={{ xs: 'flex-end', sm: 'flex-start' }} sx={{ flexShrink: 0 }}>
                    <Typography variant="caption" color="text.secondary">{item.timestamp}</Typography>
                    {item.notification.notificationType === 'home_match_welcome_message' ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" onClick={item.onAction}>Copy Welcome Message</Button>
                        <Button size="small" onClick={() => onOpenCommunications(item.notification.matchEventId)}>Open Communications</Button>
                        <Button size="small" onClick={() => void dismissNotification(item.notification, 'Welcome message resolved.')}>Resolve</Button>
                      </Stack>
                    ) : item.notification.notificationType === 'team_event_details_confirmation' ? (
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <Button size="small" variant="outlined" disabled={eventReviewBusy} onClick={item.onAction}>Review Details</Button>
                        <Button size="small" disabled={!canViewTeamEvents} onClick={() => item.notification.teamEventId && onOpenTeamEvent(item.notification.teamEventId!)}>Open Event</Button>
                      </Stack>
                    ) : !item.isBirthday && (
                      <Button size="small" variant="outlined" color={notificationSeverityColor(item.severity)} onClick={item.onAction} sx={{ fontWeight: 800 }}>
                        {item.actionLabel}
                      </Button>
                    )}
                    <Tooltip title="More actions">
                      <IconButton
                        size="small"
                        aria-label="More actions"
                        aria-controls={captainFeedMenu?.itemId === item.id ? 'captain-feed-item-menu' : undefined}
                        aria-haspopup="menu"
                        aria-expanded={captainFeedMenu?.itemId === item.id ? 'true' : undefined}
                        onClick={(event) => setCaptainFeedMenu({ anchorEl: event.currentTarget, itemId: item.id })}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Box>
              ))}
              {captainFeedItems.length === 0 && <Typography variant="body2" color="text.secondary">No captain decisions are waiting right now.</Typography>}
            </Stack>
            <Menu
              id="captain-feed-item-menu"
              anchorEl={captainFeedMenu?.anchorEl || null}
              open={Boolean(captainFeedMenu && selectedCaptainFeedItem)}
              onClose={closeCaptainFeedMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {selectedCaptainFeedItem?.onAction && selectedCaptainFeedItem.actionLabel !== 'Dismiss' && (
                <MenuItem
                  onClick={() => {
                    closeCaptainFeedMenu();
                    selectedCaptainFeedItem.onAction();
                  }}
                >
                  {selectedCaptainFeedItem.actionLabel}
                </MenuItem>
              )}
              {selectedCaptainFeedItem?.isBirthday && (
                <MenuItem
                  onClick={() => snoozeBirthdayNotification(
                    selectedCaptainFeedItem.notification,
                    addDays(todayIso(), 1),
                    'Birthday reminder snoozed until tomorrow.',
                  )}
                >
                  Snooze until tomorrow
                </MenuItem>
              )}
              {selectedCaptainFeedItem?.isBirthday && (
                <MenuItem
                  onClick={() => snoozeBirthdayNotification(
                    selectedCaptainFeedItem.notification,
                    addDays(todayIso(), 7),
                    'Birthday reminder snoozed for 1 week.',
                  )}
                >
                  Snooze 1 week
                </MenuItem>
              )}
              {selectedCaptainFeedItem?.isBirthday && (
                <MenuItem
                  onClick={() => snoozeBirthdayNotification(
                    selectedCaptainFeedItem.notification,
                    addDays(selectedBirthdaySnoozeBaseDate, 1),
                    'Birthday reminder snoozed until after the birthday.',
                  )}
                >
                  Snooze until after birthday
                </MenuItem>
              )}
              {selectedCaptainFeedItem?.notification.notificationType === 'official_provider_score_disagreement' && (
                <MenuItem onClick={() => void snoozeProviderScoreNotification(selectedCaptainFeedItem.notification)}>
                  Snooze {selectedCaptainFeedItem.notification.snoozeDays || 3} days
                </MenuItem>
              )}
              {selectedCaptainFeedItem?.onDismiss && (
                <MenuItem
                  onClick={() => {
                    closeCaptainFeedMenu();
                    selectedCaptainFeedItem.onDismiss?.();
                  }}
                >
                  Dismiss
                </MenuItem>
              )}
            </Menu>
          </Stack>
        </Paper>

        {canViewUpcomingEvents && (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Upcoming Events · {upcomingEventRows.length}</Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary">Next</Typography>
                <FormControl size="small" sx={{ minWidth: 118 }}>
                  <Select
                    value={String(upcomingEventsRangeDays)}
                    onChange={(event) => setUpcomingEventsViewState({ rangeDays: Number(event.target.value) })}
                    aria-label="Upcoming events range"
                  >
                    {[7, 14, 30, 60, 90].map((days) => (
                      <MenuItem key={days} value={String(days)}>{days} Days</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
            {upcomingEventRows.length > 0 ? (
              <Stack spacing={0} sx={{ maxHeight: 430, overflow: 'auto', pr: 0.5, borderTop: 1, borderColor: 'divider' }}>
                {upcomingEventRows.map((row) => {
                  const dateParts = formatEventDateParts(row.eventDate);
                  const timeLabel = formatTimeRange(row.startTime, row.endTime, timeFormat);
                  const attendanceLabel = row.kind === 'team-event' && row.attendance
                    ? ` · ${row.attendance.goingTotal} Going (${row.attendance.goingMen} men / ${row.attendance.goingWomen} women)`
                    : '';
                  const iconSx = eventIconStyle(row);
                  const teamAccentColor = lookupTeamAccentColor(lookups, row.teamId);
                  return (
                    <Box
                      key={`${row.kind}-${row.id}`}
                      component="button"
                      type="button"
                      onClick={row.onOpen}
                      sx={{
                        width: '100%',
                        display: 'grid',
                        gridTemplateColumns: { xs: '54px 42px minmax(0, 1fr) 24px', md: '64px 48px minmax(0, 1fr) minmax(180px, 260px) 28px' },
                        gap: { xs: 1, md: 1.5 },
                        alignItems: 'center',
                        py: 1.35,
                        pl: 1.25,
                        pr: 0,
                        border: 0,
                        borderBottom: 1,
                        borderColor: 'divider',
                        borderLeft: `5px solid ${teamAccentColor}`,
                        bgcolor: 'transparent',
                        color: 'text.primary',
                        textAlign: 'left',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      <Box>
                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 900, lineHeight: 1.2 }}>{dateParts.monthDay}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>{dateParts.weekday}</Typography>
                      </Box>
                      <Box sx={{ width: 38, height: 38, borderRadius: '50%', display: 'grid', placeItems: 'center', flexShrink: 0, ...iconSx }}>
                        {eventIcon(row)}
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 850, lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: { xs: 'normal', md: 'nowrap' } }}>
                          {row.title}
                          {attendanceLabel && <Typography component="span" variant="body2" color="text.secondary">{attendanceLabel}</Typography>}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.teamName}</Typography>
                        {(timeLabel || row.location) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'block', md: 'none' }, mt: 0.25 }}>
                            {[timeLabel, row.location].filter(Boolean).join(' • ')}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 0, textAlign: 'right' }}>
                        {timeLabel && <Typography variant="body2" sx={{ fontWeight: 850, lineHeight: 1.25 }}>{timeLabel}</Typography>}
                        {row.location && <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.location}</Typography>}
                      </Box>
                      <ChevronRightIcon fontSize="small" sx={{ color: 'text.secondary', justifySelf: 'end' }} />
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No events scheduled in the next {upcomingEventsRangeDays} days.</Typography>
            )}
          </Stack>
        </Paper>
        )}
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 1.15fr) minmax(340px, 0.85fr)' }, gap: 2 }}>
        <CaptainAddressBook teams={teamSnapshots.map((team) => ({ id: team.teamId, name: team.shortName || team.teamName, season: team.seasonLabel || '' }))} />

        <Stack spacing={2}>
          {canViewFees && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Outstanding Fees</Typography>
                <Button size="small" onClick={onOpenOutstandingFees}>View All</Button>
              </Stack>
              <Box sx={{ display: 'grid', gridTemplateColumns: '52px repeat(3, 1fr)', gap: 1.25, alignItems: 'center' }}>
                <Box sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: 'rgba(46, 125, 50, 0.10)', color: 'success.dark', display: 'grid', placeItems: 'center' }}><PaymentsIcon /></Box>
                <Box><Typography variant="h6" sx={{ fontWeight: 900 }}>{money(feesSummary.outstanding)}</Typography><Typography variant="caption" color="text.secondary">Total Outstanding</Typography></Box>
                <Box><Typography variant="h6" sx={{ fontWeight: 900 }}>{feesSummary.unpaidItems}</Typography><Typography variant="caption" color="text.secondary">Unpaid Items</Typography></Box>
                <Box><Typography variant="h6" sx={{ fontWeight: 900 }}>{feesSummary.playersOwing}</Typography><Typography variant="caption" color="text.secondary">Players Owe</Typography></Box>
              </Box>
              <Button variant="outlined" color="success" startIcon={<PaymentsIcon />} onClick={onOpenOutstandingFees} sx={{ fontWeight: 900 }}>Open Fees & Payments</Button>
            </Stack>
          </Paper>
          )}

          {quickActions.length > 0 && (
          <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
            <Stack spacing={1.5}>
              <Typography variant="h6" sx={{ fontWeight: 900 }}>Quick Actions</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
                {quickActions.map((action) => (
                  <Button key={action.label} variant="text" onClick={action.onClick} sx={{ minHeight: 78, display: 'flex', flexDirection: 'column', gap: 0.75, px: 1, color: 'text.primary' }}>
                    <Box sx={{ width: 48, height: 36, borderRadius: 1.5, bgcolor: 'rgba(46, 125, 50, 0.08)', color: 'success.dark', display: 'grid', placeItems: 'center' }}>{action.icon}</Box>
                    <Typography variant="caption" sx={{ fontWeight: 800, lineHeight: 1.15 }}>{action.label}</Typography>
                  </Button>
                ))}
              </Box>
            </Stack>
          </Paper>
          )}
        </Stack>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)' }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6" sx={{ fontWeight: 900 }}>Recent Activity</Typography>
            <Button size="small" onClick={openNotificationLog}>View All Activity</Button>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 1 }}>
            {recentActivityRows.slice(0, 5).map((row) => (
              <Box key={row.id} sx={{ display: 'grid', gridTemplateColumns: '40px minmax(0, 1fr)', gap: 1.25, p: 1, borderLeft: 1, borderColor: 'divider', minHeight: 92 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: '50%', bgcolor: 'rgba(15, 23, 42, 0.04)', color: notificationSeverityColor(row.severity as NotificationSeverity) + '.main', display: 'grid', placeItems: 'center' }}>
                  <NotificationsIcon fontSize="small" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{row.title || row.notification_type}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>{notificationLogContext(row, lookups, notificationLogMatchesById, timeFormat)}</Typography>
                  <Typography variant="caption" color="text.secondary">{new Date(row.created_at).toLocaleString()}</Typography>
                </Box>
              </Box>
            ))}
            {recentActivityRows.length === 0 && <Typography variant="body2" color="text.secondary">No recent activity has been logged yet.</Typography>}
          </Box>
        </Stack>
      </Paper>
      <Dialog open={notificationLogOpen} onClose={() => setNotificationLogOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Notification Log</DialogTitle>
        <DialogContent>
          {notificationLogLoading && <LinearProgress sx={{ mb: 2 }} />}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Created Date</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Severity</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Entity Context</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Dismissed</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Resolved</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationLogRows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{new Date(row.created_at).toLocaleString()}</TableCell>
                    <TableCell>{row.title || row.notification_type}</TableCell>
                    <TableCell>{row.category}</TableCell>
                    <TableCell>{row.severity}</TableCell>
                    <TableCell>{notificationLogContext(row, lookups, notificationLogMatchesById, timeFormat)}</TableCell>
                    <TableCell>{row.dismissed ? 'Yes' : 'No'}</TableCell>
                    <TableCell>{row.resolved ? 'Yes' : 'No'}</TableCell>
                  </TableRow>
                ))}
                {notificationLogRows.length === 0 && !notificationLogLoading && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                        No notification instances have been logged yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
      <Dialog open={Boolean(eventReview)} onClose={() => !eventReviewBusy && setEventReview(null)} fullWidth maxWidth="sm">
        <DialogTitle>Confirm Event Time &amp; Location</DialogTitle>
        <DialogContent>
          {eventReview && (
            <Stack spacing={1} sx={{ pt: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{eventReview.event.title}</Typography>
              <Typography variant="body2">Date: {formatDate(eventReview.event.event_date)}</Typography>
              <Typography variant="body2">Time: {formatTime(eventReview.event.start_time, timeFormat, eventReview.event) || 'Not set'}</Typography>
              <Typography variant="body2">Location: {[eventReview.event.facility_id && lookupLabel(lookups, 'facilities', eventReview.event.facility_id), eventReview.event.location_text].filter(Boolean).join(' • ') || 'Not set'}</Typography>
              <Typography variant="caption" color="text.secondary">Confirm only after checking these details. If the time or location changes, the reminder will return.</Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventReview(null)} disabled={eventReviewBusy}>Cancel</Button>
          <Button onClick={() => { if (eventReview?.event.id) onOpenTeamEvent(eventReview.event.id); setEventReview(null); }} disabled={eventReviewBusy}>Edit Event</Button>
          <Button variant="contained" onClick={() => void confirmTeamEventDetails()} disabled={eventReviewBusy || !eventReview?.event.start_time || !(eventReview.event.facility_id || eventReview.event.location_text?.trim())}>Confirm Details</Button>
        </DialogActions>
      </Dialog>
      <SeasonSyncReviewDialog notification={seasonSyncReview} onClose={() => setSeasonSyncReview(null)} onResolved={loadNotifications} />
      <Snackbar open={Boolean(snackMessage)} autoHideDuration={4000} message={snackMessage || ''} onClose={() => setSnackMessage(null)} />
    </Stack>
  );
}
